import { createBrowserRouter, Navigate } from "react-router-dom";
import { EMAIL_LINK_PATHS, GOOGLE_RETURN_PATH } from "@nh/shared";
import { RootLayout } from "./RootLayout";
import { LoginPage } from "@/features/auth/LoginPage";
import { RegisterPage } from "@/features/auth/RegisterPage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { VerifyEmailPage } from "@/features/auth/VerifyEmailPage";
import { GoogleCallbackPage } from "@/features/auth/GoogleCallbackPage";
import { NotFoundPage } from "@/features/not-found/NotFoundPage";
import { ShopPage } from "@/features/shop/ShopPage";
import { CheckoutPage } from "@/features/checkout/CheckoutPage";
import { OrderReturnPage } from "@/features/checkout/OrderReturnPage";
import { SoportePage } from "@/features/site-pages/SoportePage";
import { LegalPage } from "@/features/site-pages/LegalPage";
import { ProductDetailPage } from "@/features/shop/ProductDetailPage";
import { FranchisePage } from "@/features/franchise/FranchisePage";
import { FranchiseAcquirePage } from "@/features/franchise/FranchiseAcquirePage";
import { PackagesPage } from "@/features/packages/PackagesPage";
import { StorePage } from "@/features/store-front/StorePage";
import { StoreProductPage } from "@/features/store-front/StoreProductPage";
import { FranchiseeShell } from "@/features/mi-negocio/FranchiseeShell";
import { DashboardPage as MiNegocioDashboard } from "@/features/mi-negocio/DashboardPage";
import { KycPage } from "@/features/mi-negocio/KycPage";
import { RedPage } from "@/features/mi-negocio/RedPage";
import { OrdenesPage } from "@/features/mi-negocio/OrdenesPage";
import { TiendaPage } from "@/features/mi-negocio/TiendaPage";
import { SocioShell } from "@/features/socio/SocioShell";
import { SocioDashboardPage } from "@/features/socio/SocioDashboardPage";
import { RelojRecompraPage } from "@/features/socio/RelojRecompraPage";
import { MisComisionesPage } from "@/features/panel/MisComisionesPage";
import { RecursosPage } from "@/features/panel/RecursosPage";
import { PerfilPage } from "@/features/panel/PerfilPage";
import { AdminPackagesPage } from "@/features/admin/packages/AdminPackagesPage";
import { AdminRegistrationPage } from "@/features/admin/registrations/AdminRegistrationPage";
import { AdminBankAccountPage } from "@/features/admin/content/AdminBankAccountPage";
import { AdminPackageFormPage } from "@/features/admin/packages/AdminPackageFormPage";
import { AdminOrdersPage } from "@/features/admin/orders/AdminOrdersPage";
import { AdminOrderDetailPage } from "@/features/admin/orders/AdminOrderDetailPage";
import { AdminPurchasesPage } from "@/features/admin/purchases/AdminPurchasesPage";
import { AdminCouponsPage } from "@/features/admin/coupons/AdminCouponsPage";
import { AdminSedesPage } from "@/features/admin/sedes/AdminSedesPage";
import { AdminShippingPage } from "@/features/admin/shipping/AdminShippingPage";
import { AdminNetworkPage } from "@/features/admin/network/AdminNetworkPage";
import { AdminCommissionParamsPage } from "@/features/admin/commissions/AdminCommissionParamsPage";
import { AdminCommissionsPage } from "@/features/admin/commissions/AdminCommissionsPage";
import { AdminCommissionUserPage } from "@/features/admin/commissions/AdminCommissionUserPage";
import { AdminUsersPage } from "@/features/admin/users/AdminUsersPage";
import { AdminDashboardPage } from "@/features/admin/dashboard/AdminDashboardPage";
import { AdminBannerPage } from "@/features/admin/content/AdminBannerPage";
import { AdminLegalDocsPage } from "@/features/admin/content/AdminLegalDocsPage";
import { AdminSupportPage } from "@/features/admin/content/AdminSupportPage";
import { AdminAuditPage } from "@/features/admin/audit/AdminAuditPage";
import { AdminResourcesPage } from "@/features/admin/content/AdminResourcesPage";
import { AdminFranchisesPage } from "@/features/admin/franchises/AdminFranchisesPage";
import { ReferralLandingPage } from "@/features/network/ReferralLandingPage";
import { AdminProductsPage } from "@/features/admin/products/AdminProductsPage";
import { AdminProductFormPage } from "@/features/admin/products/AdminProductFormPage";
import { AdminProductDetailsPage } from "@/features/admin/products/AdminProductDetailsPage";

/**
 * Las rutas de acceso van FUERA de `RootLayout` a propósito: son
 * pantallas a sangre completa, con su propio fondo. Meterlas dentro del
 * AppShell les pondría la barra lateral de la app a alguien que todavía
 * no ha entrado.
 *
 * `GOOGLE_RETURN_PATH` y `EMAIL_LINK_PATHS` vienen del contrato: las
 * rutas a las que apuntan el regreso de Google y los enlaces de los
 * correos las define `@nh/shared`, no este archivo.
 *
 * Escribir "/verificar" y "/restablecer" a mano aquí sería el error caro:
 * el servidor COMPONE los enlaces de los correos con esas constantes, así
 * que una letra distinta en este archivo no rompe nada al compilar ni al
 * probar — solo hace que cada correo ya enviado aterrice en el 404, y se
 * descubre por el cliente que no puede verificar su cuenta.
 */
export const router = createBrowserRouter([
  /*
   * La tienda pública va también FUERA de `RootLayout`: trae su propia
   * barra superior y su propio pie, como el sitio real. El AppShell es el
   * armazón de la aplicación con sesión, no el de la tienda.
   */
  { path: "/shop", element: <ShopPage /> },
  { path: "/producto/:slug", element: <ProductDetailPage /> },
  /*
   * El cierre del pedido, réplica del `/shop/checkout` de producción:
   * información → revisión → pago (tarjeta, transferencia o efectivo).
   * Exige sesión por dentro; sin ella redirige al login con regreso.
   */
  { path: "/checkout", element: <CheckoutPage /> },
  /*
   * El regreso del pago (Stripe vuelve aquí): reconoce el folio, dice
   * que la validación es manual y, con la franquicia aprobada, abre el
   * panel de franquiciatario.
   */
  { path: "/compra/confirmacion", element: <OrderReturnPage /> },
  /*
   * Las secciones que el menú del sitio promete: soporte (el formulario
   * de contacto real) y los dos documentos legales con sus anclas.
   */
  { path: "/soporte", element: <SoportePage /> },
  { path: "/legal", element: <LegalPage /> },
  /*
   * El enlace de invitación que un socio comparte (`/r/CODE`, con el
   * código en la RUTA — un `?ref=` se pierde al reenviar). Valida forma
   * y referencia, guarda la invitación y lleva al registro.
   */
  { path: "/r/:code", element: <ReferralLandingPage /> },
  /*
   * La MISMA puerta con la estructura del sistema viejo
   * (`/register/CODE`): es la que generan los enlaces nuevos. Va ANTES
   * que `/register` en la resolución por ser más específica.
   */
  { path: "/register/:code", element: <ReferralLandingPage /> },
  { path: "/franquicias", element: <FranchisePage /> },
  /*
   * Adquirir la franquicia: la ficha técnica, el primer stock (paquete
   * mayorista del modal) y el pago sumado. Exige sesión por dentro.
   */
  { path: "/franquicias/adquirir", element: <FranchiseAcquirePage /> },
  { path: "/paquetes", element: <PackagesPage /> },
  /*
   * Las tiendas PERSONALIZADAS de los franquiciatarios: la vitrina y la
   * ficha de producto con SU precio y SU stock. Cromo propio — ver
   * `StoreLayout`.
   */
  { path: "/shop/t/:slug", element: <StorePage /> },
  { path: "/shop/t/:slug/p/:productSlug", element: <StoreProductPage /> },

  /*
   * El panel del FRANQUICIATARIO: mismo armazón, otro mapa. La guarda
   * (sesión + franquicia validada) vive en `FranchiseeShell`.
   */
  {
    path: "/mi-negocio",
    element: <FranchiseeShell />,
    children: [
      { index: true, element: <MiNegocioDashboard /> },
      { path: "kyc", element: <KycPage /> },
      { path: "red", element: <RedPage /> },
      { path: "ordenes", element: <OrdenesPage /> },
      { path: "reloj", element: <RelojRecompraPage /> },
      { path: "tienda", element: <TiendaPage /> },
      { path: "comisiones", element: <MisComisionesPage /> },
      { path: "recursos", element: <RecursosPage /> },
      { path: "perfil", element: <PerfilPage /> },
    ],
  },

  /*
   * El panel del SOCIO (Anahí, 27-ago): la casa de cualquier sesión —
   * métricas, pedidos, red, comisiones, recursos, KYC y perfil, con la
   * puerta a la franquicia siempre al final. Comparte páginas con
   * `/mi-negocio` vía `features/panel/`.
   */
  {
    path: "/socio",
    element: <SocioShell />,
    children: [
      { index: true, element: <SocioDashboardPage /> },
      { path: "pedidos", element: <OrdenesPage /> },
      { path: "reloj", element: <RelojRecompraPage /> },
      { path: "red", element: <RedPage /> },
      { path: "comisiones", element: <MisComisionesPage /> },
      { path: "recursos", element: <RecursosPage /> },
      { path: "kyc", element: <KycPage /> },
      { path: "perfil", element: <PerfilPage /> },
    ],
  },

  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: EMAIL_LINK_PATHS.resetPassword, element: <ResetPasswordPage /> },
  { path: EMAIL_LINK_PATHS.verifyEmail, element: <VerifyEmailPage /> },
  { path: GOOGLE_RETURN_PATH, element: <GoogleCallbackPage /> },
  {
    path: "/",
    element: <RootLayout />,
    children: [
      /*
       * El panel entra por productos. Antes entraba por "/health", que
       * era una pantalla de diagnóstico; se retiró junto al escaparate
       * del design system (Anahí, 26-ago) ahora que la tienda va a ser
       * funcional y el panel tiene trabajo de verdad que enseñar.
       */
      /*
       * La puerta del panel es el DASHBOARD (Anahí, 27-ago): el estado
       * del negocio de un vistazo, no una lista de productos.
       */
      /*
       * La raíz del DOMINIO es la tienda (14-sep-2026): cuando
       * itsthebest1111.com apunte aquí, `/` no puede llevar al panel.
       * El admin entra por /admin, que ya conoce.
       */
      { index: true, element: <Navigate to="/shop" replace /> },
      { path: "admin", element: <AdminDashboardPage /> },

      /*
       * Panel de administración. Hoy solo existe el rol super-admin y solo
       * la sección de productos; de ahí que las rutas cuelguen de
       * `admin/products` y no de un índice de panel que todavía no existe.
       */
      { path: "admin/products", element: <AdminProductsPage /> },
      { path: "admin/products/new", element: <AdminProductFormPage /> },
      { path: "admin/products/:id", element: <AdminProductFormPage /> },
      /*
       * La segunda mitad de un producto: los once bloques de su ficha
       * larga. Cuelga del id porque se escribe SOBRE un producto que ya
       * existe — no hay `/new/detalles` porque no habría nada que
       * detallar. Ver `ProductTabs`.
       */
      { path: "admin/products/:id/detalles", element: <AdminProductDetailsPage /> },

      /*
       * Órdenes y Compras: en el sidebar desde ya (Anahí, 26-ago), con
       * estado vacío hasta que exista el checkout que las llene.
       */
      { path: "admin/orders", element: <AdminOrdersPage /> },
      /*
       * El detalle: ver la orden, validar el pago con su comprobante y
       * cambiarle el estado a mano. Ver `AdminOrderDetailPage`.
       */
      { path: "admin/orders/:id", element: <AdminOrderDetailPage /> },
      { path: "admin/purchases", element: <AdminPurchasesPage /> },
      /*
       * Cupones y sedes (Anahí, 27-ago): réplica de `admin/coupons` y
       * `admin/manage-sedes` de producción. Los cupones alimentan el
       * checkout; las sedes esperan a la integración de envíos.
       */
      { path: "admin/coupons", element: <AdminCouponsPage /> },
      { path: "admin/sedes", element: <AdminSedesPage /> },
      // Envíos (13-oct-2026): sede de origen y cajas para cotizar con Trasso.
      { path: "admin/envios", element: <AdminShippingPage /> },
      /*
       * El unilevel y los parámetros del plan (Anahí, 27-ago): réplicas
       * de `admin/network/unilevel` y `admin/v2/commission-params`.
       */
      { path: "admin/network", element: <AdminNetworkPage /> },
      { path: "admin/commission-params", element: <AdminCommissionParamsPage /> },
      /*
       * El resto del panel de producción (Anahí, 27-ago): el censo de
       * cuentas, el libro de comisiones y las franquicias con su
       * configuración — la que el checkout de franquicia cobra.
       */
      { path: "admin/users", element: <AdminUsersPage /> },
      { path: "admin/commissions", element: <AdminCommissionsPage /> },
      /* El expediente de comisiones POR PERSONA: pagar y comprobantes. */
      { path: "admin/commissions/u/:userId", element: <AdminCommissionUserPage /> },
      { path: "admin/franchises", element: <AdminFranchisesPage /> },
      /*
       * La configuración vive ahora como PESTAÑA dentro de Franquicias
       * (Anahí, 27-ago); la ruta vieja redirige para no romper marcadores.
       */
      { path: "admin/franchise-settings", element: <Navigate to="/admin/franchises" replace /> },
      /*
       * El contenido del sitio y el buzón: banner, documentos legales,
       * soporte y la biblioteca de recursos (Anahí, 27-ago).
       */
      { path: "admin/banner", element: <AdminBannerPage /> },
      { path: "admin/legal-docs", element: <AdminLegalDocsPage /> },
      { path: "admin/support", element: <AdminSupportPage /> },
      { path: "admin/resources", element: <AdminResourcesPage /> },
      /* La Bitácora (2-oct-2026): peticiones, errores agrupados y lista negra. */
      { path: "admin/bitacora", element: <AdminAuditPage /> },

      { path: "admin/packages", element: <AdminPackagesPage /> },
      { path: "admin/registros", element: <AdminRegistrationPage /> },
      { path: "admin/cuenta-bancaria", element: <AdminBankAccountPage /> },
      { path: "admin/packages/new", element: <AdminPackageFormPage /> },
      { path: "admin/packages/:id", element: <AdminPackageFormPage /> },
      /* `/health` era el tablero de desarrollo del arranque: el enlace
         viejo lleva a la tienda, no a un 404. */
      { path: "health", element: <Navigate to="/shop" replace /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
